import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { Button } from '../button';
import { Icon } from '../icon';

import {
  StyledTooltip,
  TooltipV2Container,
  TooltipBubble,
  TooltipBubblePortal,
  TooltipContent,
  TooltipArrow,
  TooltipText,
  TooltipIconContainer,
  TooltipActions,
} from './styles';

import { ICONS } from '@controlrooms/constants';
import { unique } from '@controlrooms/utils';

interface TooltipProps {
  className?: string;
  children: React.ReactNode;
  delay?: string;
  label: string | React.ReactElement;
  offset?: string;
  place?: string;
  type?: 'primary' | 'secondary';
  testid?: string;
  theme?: string;
  // V2 props
  version?: 'v1' | 'v2';
  delayShow?: number; // ms - show tooltip only after this hover delay (v2 only)
  direction?: 'right' | 'left' | 'up' | 'down';
  renderInPortal?: boolean; // render bubble in portal to avoid clipping by overflow ancestors (v2 only)
  tooltipType?: 'text' | 'actionable';
  prefixIcon?: boolean;
  prefixIconName?: typeof ICONS[keyof typeof ICONS];
  onActionClick?: () => void;
  actionIconName?: typeof ICONS[keyof typeof ICONS];
}

export const Tooltip = React.forwardRef<HTMLSpanElement, TooltipProps>(
  (
    {
      className,
      children,
      delay,
      label,
      offset,
      place,
      type = 'primary',
      testid,
      theme,
      version = 'v1',
      delayShow,
      direction = 'right',
      renderInPortal = false,
      tooltipType = 'text',
      prefixIcon = false,
      prefixIconName = ICONS.Scissor,
      onActionClick,
      actionIconName = ICONS.Gear,
    },
    ref,
  ) => {
    const _label = typeof label === 'string' ? label : ReactDOMServer.renderToString(label);
    const id = unique();

    const [show, setShow] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLSpanElement | null>(null);
    const [portalPosition, setPortalPosition] = useState<{
      left: number;
      top: number;
      transform: string;
    } | null>(null);

    const updatePortalPosition = useCallback(() => {
      const el = containerRef.current ?? (ref as React.RefObject<HTMLSpanElement>)?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const gap = 8;
      switch (direction) {
        case 'left':
          setPortalPosition({
            left: rect.left - gap,
            top: rect.top + rect.height / 2,
            transform: 'translate(-100%, -50%)',
          });
          break;
        case 'up':
          setPortalPosition({
            left: rect.left + rect.width / 2,
            top: rect.top - gap,
            transform: 'translate(-50%, -100%)',
          });
          break;
        case 'down':
          setPortalPosition({
            left: rect.left + rect.width / 2,
            top: rect.top + rect.height + gap,
            transform: 'translate(-50%, 0)',
          });
          break;
        case 'right':
        default:
          setPortalPosition({
            left: rect.right + gap,
            top: rect.top + rect.height / 2,
            transform: 'translate(0, -50%)',
          });
      }
    }, [direction, ref]);

    useEffect(() => {
      if (!renderInPortal || !show) return;
      updatePortalPosition();
      const onUpdate = () => updatePortalPosition();
      window.addEventListener('scroll', onUpdate, true);
      window.addEventListener('resize', onUpdate);
      return () => {
        window.removeEventListener('scroll', onUpdate, true);
        window.removeEventListener('resize', onUpdate);
      };
    }, [renderInPortal, show, updatePortalPosition]);

    // Legacy V1 implementation
    if (version === 'v1') {
      return (
        <>
          <ReactTooltip id={id} />
          <StyledTooltip
            data-tip={_label}
            data-testid={testid}
            data-for={id}
            data-html={typeof label !== 'string'}
            delayShow={delay}
            className={className}
            data-offset={offset}
            data-place={place}
            type={type}
            _theme={theme}
            ref={ref}
          >
            {children}
          </StyledTooltip>
        </>
      );
    }

    const handleMouseEnter = () => {
      if (renderInPortal) {
        if (delayShow == null || delayShow <= 0) {
          setShow(true);
        } else {
          timeoutRef.current = setTimeout(() => setShow(true), delayShow);
        }
        return;
      }
      if (delayShow == null || delayShow <= 0) return;
      timeoutRef.current = setTimeout(() => setShow(true), delayShow);
    };
    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShow(false);
    };

    const showBubble = renderInPortal ? show : delayShow != null ? show : undefined;

    const bubbleContent = (
      <>
        <TooltipArrow direction={direction} />
        <TooltipContent>
          {prefixIcon && (
            <TooltipIconContainer>
              <Icon name={prefixIconName} width="14" height="14" />
            </TooltipIconContainer>
          )}
          <TooltipText tooltipType={tooltipType}>{label}</TooltipText>
          {tooltipType === 'actionable' && onActionClick && (
            <TooltipActions>
              <Button
                buttonType="icon"
                buttonSize="small"
                iconName={actionIconName}
                onClick={onActionClick}
              />
            </TooltipActions>
          )}
        </TooltipContent>
      </>
    );

    return (
      <>
        <TooltipV2Container
          className={className}
          data-testid={testid}
          ref={(node) => {
            (containerRef as React.MutableRefObject<HTMLSpanElement | null>).current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLSpanElement | null>).current = node;
          }}
          $show={showBubble}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
          {!renderInPortal && (
            <TooltipBubble direction={direction} tooltipType={tooltipType}>
              {bubbleContent}
            </TooltipBubble>
          )}
        </TooltipV2Container>
        {renderInPortal &&
          show &&
          portalPosition &&
          ReactDOM.createPortal(
            <TooltipBubblePortal
              tooltipType={tooltipType}
              $left={portalPosition.left}
              $top={portalPosition.top}
              $transform={portalPosition.transform}
            >
              {bubbleContent}
            </TooltipBubblePortal>,
            document.body,
          )}
      </>
    );
  },
);
